import { useState, useRef, useEffect } from 'react';
import styles from './TestimonialsGrid.module.css';
import useScrollReveal from '../../hooks/useScrollReveal';

import cert1 from '../../images/certificate_1.webp';
import cert2 from '../../images/certificate_2.webp';
import cert4 from '../../images/certificate_4.webp';

const certImages = [cert1, cert2, cert4];

// 3 columns  -  alternating images & videos for visual balance
const columns = [
  [
    { type: 'img', src: cert1, idx: 0 },
    { type: 'video', id: 'eY9ed3e76lw' },
  ],
  [
    { type: 'video', id: 'da2Fu69zf58' },
    { type: 'img', src: cert2, idx: 1 },
  ],
  [
    { type: 'img', src: cert4, idx: 2 },
    { type: 'video', id: 'KMiJfapTT1o' },
  ],
];

function TestimonialsGrid({ dark = false }) {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = () => setLightboxIndex((i) => (i - 1 + certImages.length) % certImages.length);
  const goNext = () => setLightboxIndex((i) => (i + 1) % certImages.length);

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'ArrowLeft') goNext();
      if (e.key === 'ArrowRight') goPrev();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);

  return (
    <>
      <div className={`${styles.wrapper} ${dark ? styles.wrapperDark : ''}`} ref={sectionRef}>
        {/* Header */}
        {!dark && (
          <div className={styles.header} data-sr="0">
            <h2 className={styles.title}>
              ככה הן הגיבו <em className={styles.em}>כשהן יצאו ממני</em>
            </h2>
          </div>
        )}

        {/* Masonry grid */}
        <div className={styles.masonryGrid}>
          {columns.map((col, ci) => (
            <div key={ci} className={styles.masonryCol}>
              {col.map((item, ri) => (
                item.type === 'img' ? (
                  <div
                    key={ri}
                    className={styles.card}
                    data-sr={String(0.06 + ci * 0.08 + ri * 0.05)}
                    onClick={() => openLightbox(item.idx)}
                    role="button"
                    tabIndex={0}
                  >
                    <img src={item.src} alt="המלצה" className={styles.cardImg} loading="lazy" />
                    <div className={styles.cardOverlay}>
                      <span className={styles.cardZoom}>⊕</span>
                    </div>
                  </div>
                ) : (
                  <div key={ri} className={styles.videoCard} data-sr={String(0.06 + ci * 0.08 + ri * 0.05)}>
                    <iframe
                      className={styles.iframe}
                      src={`https://www.youtube.com/embed/${item.id}`}
                      title="המלצת תלמידה"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className={styles.lightboxOverlay}
          onClick={closeLightbox}
        >
          <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.lightboxClose} onClick={closeLightbox}>✕</button>

            <button className={styles.lightboxArrow} style={{ right: '-60px' }} onClick={goPrev}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <img
              src={certImages[lightboxIndex]}
              alt={`המלצה ${lightboxIndex + 1}`}
              className={styles.lightboxImage}
            />

            <button className={styles.lightboxArrow} style={{ left: '-60px' }} onClick={goNext}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className={styles.lightboxCounter}>
              {lightboxIndex + 1} / {certImages.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TestimonialsGrid;
